/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  BuildOrganizationHierarchyReq,
  PolicyReader,
  ValidateOrganizationReq
} from '@src/endpoints/services/policyReader.service'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import type { State } from '@src/redux/configureStore'
import { parseOrganization, parseScopeAccounts } from './helpers'
import type * as Models from './models'
import { cloneDeep, isEmpty, isEqual, some } from 'lodash'
import {
  CloudTypeEnum,
  OrganizationInfo_IncludeAccountIdListEntry,
  ValidateTypeEnum
} from '@src/endpoints/services/base.service'
import { TransferModel } from '@src/modules/accounts/models'
import uuid from 'uuid/v4'
import type { CorrelatedSource } from '@src/endpoints/openapis/saas-cam-correlated-sources'

const getSuccessMessage = (msg: string): Models.Messages => ({
  title: 'Success!',
  type: 'success',
  message: msg
})

export const initialState = {
  uidEditor: null as string,
  isScopes: false,
  scopesData: {
    search: '',
    filter: {
      label: 'All',
      value: 'all'
    },
    select: 'allAccounts'
  },
  isNew: false,
  enableScopeAccounts: false,
  model: {} as Models.Model,
  scopeAccounts: [] as Models.ScopeAccount[],
  isTesting: false as boolean,
  testConnectionSuccess: false as boolean,
  validateType: null as ValidateTypeEnum,
  messages: {
    title: null as string | undefined,
    type: null as Models.ErrorType | undefined,
    message: null as string | undefined
  } as Models.Messages,
  fileName: null as string,
  progressFileLoad: null as number,
  apiResponse: false,
  viewOnly: false,
  isFetching: false as boolean,
  org_build_uuid: null as string,
  isOrg: false
}

export const defaultSecret = '****************'

export type InitialState = typeof initialState
type PartialState = Partial<Omit<InitialState, 'messages'>> & {
  messages?: Partial<InitialState['messages']>
}

interface PayloadData extends Models.OrgStructure {}

interface GetDataPayload {
  model: Models.Model
  validateType: ValidateTypeEnum
  enableScopeAccounts: boolean
  scopeAccounts: Models.ScopeAccount[]
  messages: Models.Messages
  isTesting: boolean
  isNew: boolean
  apiResponse: boolean
  viewOnly?: boolean
  isFetching?: boolean
  org_build_uuid?: string
  testConnectionSuccess?: boolean
}

export function updateSecret(newState, state) {
  switch (state.model.cloud_type) {
    case CloudTypeEnum.AWS: {
      if (state?.model?.awsInfo?.external_id) {
        newState.model.awsInfo.external_id = state.model.awsInfo.external_id
      }
      break
    }
    case CloudTypeEnum.AZURE: {
      if (state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      }
      break
    }
    case CloudTypeEnum.GCP: {
      if (state?.model?.gcpInfo?.gcp_json_file_text) {
        newState.model.gcpInfo.gcp_json_file_text =
          state.model.gcpInfo.gcp_json_file_text
      }
      break
    }
    case CloudTypeEnum.IDP_OKTA: {
      if (state?.model?.oktaInfo?.token) {
        newState.model.oktaInfo.token = state?.model?.oktaInfo?.token
      }
      break
    }
    case CloudTypeEnum.IDP_AZURE_AD: {
      if (state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      }
      break
    }
    default: {
      break
    }
  }
  return newState
}

function getNewChangeState(
  state,
  action: PayloadAction<PartialState & { model: Models.Model }>
) {
  const newState = cloneDeep({
    ...state,
    ...(action.payload as InitialState)
  })
  if (action.payload.isNew) {
    newState.model = cloneDeep(action.payload.model)
    updateSecret(newState, state)
    return newState
  }
  const apiResponse = action.payload?.apiResponse
  switch (action.payload.model.cloud_type) {
    case CloudTypeEnum.AWS: {
      if (apiResponse && action?.payload?.model?.awsInfo?.external_id) {
        newState.model.awsInfo.external_id = state.model.awsInfo.external_id
      } else if (apiResponse && state?.model?.awsInfo?.external_id) {
        newState.model.awsInfo.external_id = state.model.awsInfo.external_id
      } else if (apiResponse) {
        newState.model.awsInfo.external_id = defaultSecret
      }
      break
    }
    case CloudTypeEnum.AZURE: {
      if (apiResponse && action?.payload?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      } else if (apiResponse && state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      } else if (apiResponse) {
        newState.model.azureInfo.app_secret = defaultSecret
      }
      break
    }
    case CloudTypeEnum.GCP: {
      if (apiResponse && action?.payload?.model?.gcpInfo?.gcp_json_file_text) {
        newState.model.gcpInfo.gcp_json_file_text =
          state.model.gcpInfo.gcp_json_file_text
      } else if (apiResponse && state?.model?.gcpInfo?.gcp_json_file_text) {
        newState.model.gcpInfo.gcp_json_file_text =
          state.model.gcpInfo.gcp_json_file_text
      } else if (apiResponse) {
        newState.model.gcpInfo.gcp_json_file_text = defaultSecret
      }
      break
    }
    case CloudTypeEnum.IDP_OKTA: {
      if (apiResponse && action?.payload?.model?.oktaInfo?.token) {
        newState.model.oktaInfo.token = state.model.oktaInfo.token
        newState.model.oktaIdpInfo.token = state.model.oktaIdpInfo.token
      } else if (apiResponse && state?.model?.oktaInfo?.token) {
        newState.model.oktaInfo.token = state.model.oktaInfo.token
      } else if (apiResponse) {
        newState.model.oktaInfo.token = defaultSecret
      }
      break
    }
    case CloudTypeEnum.IDP_AZURE_AD: {
      if (apiResponse && action?.payload?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      } else if (apiResponse && state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret = state.model.azureInfo.app_secret
      } else if (apiResponse) {
        newState.model.azureInfo.app_secret = defaultSecret
      }
      break
    }
    default: {
      break
    }
  }
  return newState
}

function getNewStateModel(
  state,
  action: PayloadAction<PartialState & { model: Models.Model }>
) {
  const newState = cloneDeep({
    ...state,
    ...(action.payload as InitialState)
  })
  switch (state.model.cloud_type) {
    case CloudTypeEnum.AWS: {
      if (action.payload?.isNew) {
        newState.model.awsInfo.external_id = state?.model?.awsInfo?.external_id
      } else if (state?.model?.awsInfo?.external_id) {
        newState.model.awsInfo.external_id = state?.model?.awsInfo?.external_id
      } else {
        newState.model.awsInfo.external_id = defaultSecret
      }
      break
    }
    case CloudTypeEnum.AZURE: {
      if (action.payload?.isNew) {
        newState.model.azureInfo.app_secret =
          state?.model?.azureInfo?.app_secret
      } else if (state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret =
          state?.model?.azureInfo?.app_secret
      } else {
        newState.model.azureInfo.app_secret = defaultSecret
      }
      break
    }
    case CloudTypeEnum.GCP: {
      if (action.payload?.isNew) {
        newState.model.gcpInfo.gcp_json_file_text =
          state?.model?.gcpInfo?.gcp_json_file_text
      } else if (state?.model?.gcpInfo?.gcp_json_file_text) {
        newState.model.gcpInfo.gcp_json_file_text =
          state?.model?.gcpInfo?.gcp_json_file_text
      } else {
        newState.model.gcpInfo.gcp_json_file_text = defaultSecret
      }
      break
    }
    case CloudTypeEnum.IDP_OKTA: {
      if (action.payload?.isNew) {
        newState.model.oktaInfo.token = state?.model?.oktaInfo?.token
      } else if (state?.model?.oktaInfo?.token) {
        newState.model.oktaInfo.token = state?.model?.oktaInfo?.token
      } else {
        newState.model.oktaInfo.token = defaultSecret
      }
      break
    }
    case CloudTypeEnum.IDP_AZURE_AD: {
      if (action.payload?.isNew) {
        newState.model.azureInfo.app_secret =
          state?.model?.azureInfo?.app_secret
      } else if (state?.model?.azureInfo?.app_secret) {
        newState.model.azureInfo.app_secret =
          state?.model?.azureInfo?.app_secret
      } else {
        newState.model.azureInfo.app_secret = defaultSecret
      }
      break
    }
    default: {
      break
    }
  }
  return newState.model
}

export function setEditFormSecretToDefaultSecret(newState) {
  if (newState.isNew) {
    return newState
  }
  switch (newState.model.cloud_type) {
    case CloudTypeEnum.AWS: {
      newState.model.awsInfo.external_id = defaultSecret
      break
    }
    case CloudTypeEnum.AZURE: {
      newState.model.azureInfo.app_secret = defaultSecret
      break
    }
    case CloudTypeEnum.GCP: {
      newState.model.gcpInfo.gcp_json_file_text = defaultSecret
      break
    }
    case CloudTypeEnum.IDP_AZURE_AD: {
      newState.model.azureInfo.app_secret = defaultSecret
      break
    }
    case CloudTypeEnum.IDP_OKTA: {
      newState.model.oktaInfo.token = defaultSecret
      break
    }
    default:
      break
  }
  return newState
}

export const organizationEditor = createSlice({
  name: 'organizationEditor',
  initialState,
  reducers: {
    select: (state, action: any) => ({
      ...state,
      scopesData: {
        ...state.scopesData,
        select: action.payload.select
      }
    }),
    filter: (state, action: any) => ({
      ...state,
      scopesData: {
        ...state.scopesData,
        filter: action.payload.filter
      }
    }),
    search: (state, action: any) => ({
      ...state,
      scopesData: {
        ...state.scopesData,
        search: action.payload.search
      }
    }),
    changeState: (
      state,
      action: PayloadAction<
        PartialState & {
          model: Models.Model
        }
      >
    ): InitialState => getNewChangeState(state, action),
    openEditor: (
      state,
      action: PayloadAction<PartialState & { model: Models.Model }>
    ): InitialState => {
      const newState = cloneDeep({
        ...state,
        messages: {
          title: null,
          message: null,
          type: null
        },
        ...(action.payload as InitialState),
        uidEditor: uuid(),
        isScopes: action.payload.isScopes || false,
        isOrg: action.payload.isOrg || false
      })

      return setEditFormSecretToDefaultSecret(newState)
    },
    closeEditor: (
      state,
      action: PayloadAction<PartialState>
    ): InitialState => ({
      ...state,
      model: null,
      validateType: null,
      messages: {
        title: null,
        message: null,
        type: null
      },
      ...(action.payload as InitialState),
      uidEditor: null
    }),
    testPending: (state, action: PayloadAction<{ isTesting: boolean }>) => {
      state.isTesting = action.payload.isTesting
    },
    testConnectionSuccess: (
      state,
      action: PayloadAction<{ testConnectionSuccess: boolean }>
    ) => {
      state.testConnectionSuccess = action.payload.testConnectionSuccess
    },
    setFetchingFlag: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload
    },
    setCorrelatedSource: (
      state,
      action: PayloadAction<{ correlatedSource: CorrelatedSource }>
    ) => ({
      ...state,
      model: {
        ...state.model,
        correlatedSource: action?.payload?.correlatedSource
      }
    }),
    enableScopeAccounts: (
      state,
      action: PayloadAction<{ checked: boolean }>
    ) => {
      state.enableScopeAccounts = action.payload.checked
      state.model.includeAccountIdList = !action?.payload?.checked
        ? parseScopeAccounts({
            scopeAccounts: state.scopeAccounts,
            defaultValue: action.payload.checked
          })
        : state?.model?.includeAccountIdList
    },
    tested: (state, action: PayloadAction<GetDataPayload>) => {
      state.model = getNewStateModel(state, action)
      state.isTesting = action.payload.isTesting
      state.isFetching = action.payload.isFetching
      state.scopeAccounts = action.payload.scopeAccounts
      state.messages = action.payload?.messages || initialState.messages
      state.validateType = action.payload.validateType
      state.testConnectionSuccess = action.payload.testConnectionSuccess
    },

    afterTested: (
      state,
      action: PayloadAction<{
        scopeAccounts: Models.ScopeAccount[]
        enableScopeAccounts: boolean
        includeAccountIds: OrganizationInfo_IncludeAccountIdListEntry
      }>
    ) => {
      state.enableScopeAccounts = action.payload.enableScopeAccounts
      state.scopeAccounts = action.payload.scopeAccounts
      state.model.includeAccountIdList = action.payload.includeAccountIds
    },

    update: (
      state,
      action: PayloadAction<
        Partial<Models.Model> & {
          includeAccountIdList: OrganizationInfo_IncludeAccountIdListEntry
        }
      >
    ): InitialState => ({
      ...state,
      model: {
        ...state.model,
        ...action.payload
      }
    }),
    updateOrgBuildUuid: (
      state,
      action: PayloadAction<{
        org_build_uuid
      }>
    ): InitialState => ({
      ...state,
      org_build_uuid: action.payload.org_build_uuid
    }),
    messages: (
      state,
      action: PayloadAction<{
        isTesting?: boolean
        isFetching?: boolean
        title: string
        type: Models.ErrorType
        message?: string
      }>
    ) => {
      state.messages.title = action.payload.title
      state.messages.type = action.payload.type
      state.messages.message = action.payload.message
    },
    dispose: (state): InitialState => initialState,
    disposeMessages: (state): InitialState => ({
      ...state,
      messages: initialState?.messages
    })
  }
})

export const splitTestingConnection =
  (
    params: ValidateOrganizationReq,
    showMessage = true,
    abortSignal: AbortSignal
  ) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(actions.disposeMessages())
    dispatch(organizationEditor.actions.testPending({ isTesting: true }))
    PolicyReader.ValidateOrganization({
      params,
      options: {
        signal: abortSignal || window.ABORT_CONTROLLER?.signal
      }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          message => {
            dispatch(
              organizationEditor.actions.messages({
                isTesting: false,
                message:
                  'Please verify the source account information and configuration. Read more about ',
                type: 'error',
                title: 'Test Connection Failed'
              })
            )
          },
          false
        )
      )
      .then(response => {
        const { organization_info, validate_type, account_info } = response
        const { organization } = getStore()
        const { editor } = organization
        const { messages, isNew, model = {} as Models.Model } = editor
        switch (validate_type) {
          case ValidateTypeEnum.VALIDATE_TYPE_ACCOUNT: {
            const message =
              'Your credentials have been validated. Select Save to use this configuration'
            dispatch(
              organizationEditor.actions.changeState({
                isNew,
                apiResponse: true,
                isTesting: false,
                testConnectionSuccess: true,
                validateType: validate_type,
                messages: showMessage ? getSuccessMessage(message) : messages,
                model: {
                  ...new TransferModel(account_info).convertToModel(),
                  correlatedSource: model?.correlatedSource
                }
              })
            )
            dispatch(
              actions.testConnectionSuccess({ testConnectionSuccess: true })
            )
            break
          }
          case ValidateTypeEnum.VALIDATE_TYPE_ORGANIZATION: {
            const message = `Testing Success`
            dispatch(
              organizationEditor.actions.changeState({
                isNew,
                apiResponse: true,
                isTesting: true,
                testConnectionSuccess: true,
                isFetching: true,
                validateType: validate_type,
                messages: showMessage ? getSuccessMessage(message) : messages,
                model: {
                  ...new TransferModel(organization_info).convertToModel(),
                  includeAccountIdList: {
                    ...editor.model.includeAccountIdList
                  },
                  correlatedSource: model?.correlatedSource
                }
              })
            )

            const {
              organization_name,
              cloud_type,
              organization_id,
              aws_organization_info,
              azure_organization_info,
              gcp_organization_info
            } = organization_info

            if (
              gcp_organization_info &&
              params?.gcp_organization_info?.gcp_json_file_text &&
              !isEqual(
                params?.gcp_organization_info?.gcp_json_file_text,
                defaultSecret
              )
            ) {
              gcp_organization_info.gcp_json_file_text =
                params?.gcp_organization_info?.gcp_json_file_text
            }

            if (
              aws_organization_info &&
              params?.aws_organization_info?.external_id &&
              !isEqual(
                params?.aws_organization_info?.external_id,
                defaultSecret
              )
            ) {
              aws_organization_info.external_id =
                params?.aws_organization_info?.external_id
            }

            if (
              azure_organization_info &&
              params?.azure_organization_info?.app_secret &&
              !isEqual(
                params?.azure_organization_info?.app_secret,
                defaultSecret
              )
            ) {
              azure_organization_info.app_secret =
                params?.azure_organization_info?.app_secret
            }

            const buildOrgHierarchyParams: BuildOrganizationHierarchyReq = {
              organization_name,
              cloud_type,
              organization_id,
              aws_organization_info,
              azure_organization_info,
              gcp_organization_info
            }

            PolicyReader.BuildOrganizationHierarchy({
              params: buildOrgHierarchyParams,
              options: {},
              makeRequestOptions: {}
            })
              .then(res =>
                defaultHandleStatus(
                  res,
                  message => {
                    dispatch(
                      organizationEditor.actions.messages({
                        isTesting: false,
                        isFetching: false,
                        message:
                          'Please verify the source account information and configuration. Read more about ',
                        type: 'error',
                        title: 'Test Connection Failed'
                      })
                    )
                    dispatch(
                      actions.testConnectionSuccess({
                        testConnectionSuccess: false
                      })
                    )
                  },
                  false
                )
              )
              .then(response => {
                const { org_build_uuid } = response
                dispatch(
                  organizationEditor.actions.updateOrgBuildUuid({
                    org_build_uuid
                  })
                )
              })
              .catch(err => {
                dispatch(
                  organizationEditor.actions.messages({
                    isTesting: false,
                    isFetching: false,
                    message:
                      'Please verify the source account information and configuration. Read more about ',
                    type: 'error',
                    title: 'Test Connection Failed'
                  })
                )
                dispatch(
                  actions.testConnectionSuccess({
                    testConnectionSuccess: false
                  })
                )
              })
            break
          }
          default: {
            break
          }
        }
        dispatch(
          organizationEditor.actions.testPending({
            isTesting: false
          })
        )
      })
      .catch((res: Response) => {
        dispatch(
          organizationEditor.actions.testPending({
            isTesting: false
          })
        )
      })
  }

export const testing =
  (
    params: ValidateOrganizationReq,
    showMessage = true,
    abortSignal: AbortSignal
  ) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(organizationEditor.actions.testPending({ isTesting: true }))
    PolicyReader.ValidateOrganization({
      params,
      options: {
        signal: abortSignal || window.ABORT_CONTROLLER?.signal
      }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          message => {
            dispatch(
              organizationEditor.actions.messages({
                isTesting: false,
                message,
                type: 'error',
                title: 'Test Connection Failed'
              })
            )
          },
          false
        )
      )
      .then(response => {
        const { organization_info, validate_type, account_info, error } =
          response

        if (error) {
          dispatch(
            organizationEditor.actions.messages({
              isTesting: false,
              message:
                'Please verify the source account information and configuration. Read more about ',
              type: 'error',
              title: 'Test Connection Failed'
            })
          )
          dispatch(
            organizationEditor.actions.testPending({
              isTesting: false
            })
          )
          return
        }
        const { organization } = getStore()
        const { editor } = organization
        const {
          messages,
          isNew,
          enableScopeAccounts,
          model = {} as Models.Model
        } = editor
        const editorType: string = isNew ? 'creation' : 'update'
        switch (validate_type) {
          case ValidateTypeEnum.VALIDATE_TYPE_ACCOUNT: {
            const message =
              'Your credentials have been validated. Select Save to use this configuration'
            dispatch(
              organizationEditor.actions.changeState({
                isNew,
                apiResponse: true,
                isTesting: false,
                testConnectionSuccess: true,
                validateType: validate_type,
                messages: showMessage ? getSuccessMessage(message) : messages,
                model: {
                  ...new TransferModel(account_info).convertToModel(),
                  correlatedSource: model?.correlatedSource
                }
              })
            )
            break
          }
          case ValidateTypeEnum.VALIDATE_TYPE_ORGANIZATION: {
            const { original = {}, scopeAccounts = [] }: PayloadData =
              parseOrganization(organization_info)
            const { includeAccountIdList = {} } = model
            const includeAllScopeAccounts = !enableScopeAccounts || isNew
            const includeAccountIds = parseScopeAccounts({
              scopeAccounts,
              includeAccountIds: includeAccountIdList,
              defaultValue: includeAllScopeAccounts
            })
            const showScopeAccounts =
              !isEmpty(includeAccountIds) &&
              some(includeAccountIds, x => !x) &&
              some(includeAccountIds, x => x)
            const message = `Click save to finalize organization ${editorType}`
            dispatch(
              organizationEditor.actions.tested({
                isNew,
                apiResponse: true,
                isTesting: false,
                isFetching: false,
                testConnectionSuccess: true,
                validateType: validate_type,
                messages: showMessage ? getSuccessMessage(message) : messages,
                model: {
                  ...new TransferModel(original).convertToModel(),
                  includeAccountIdList: includeAccountIds,
                  correlatedSource: model?.correlatedSource
                },
                scopeAccounts,
                enableScopeAccounts: showScopeAccounts
              })
            )
            break
          }
          default: {
            break
          }
        }
      })
      .catch((res: Response) => {
        dispatch(
          organizationEditor.actions.testPending({
            isTesting: false
          })
        )
      })
  }

export const { reducer, actions, caseReducers } = organizationEditor

export default organizationEditor
